import React, { useState, useContext, useEffect } from 'react';
import { usePopper } from 'react-popper';

import { Delimiter } from 'src/kit';
import * as S from './Dropdown.styled';

export const DropdownContext = React.createContext<any>({});
export type DropdownProps = React.ComponentProps<typeof S.StyledContainer> & {
  children: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  closeOnSelect?: boolean;
  placement?: string;
  disabled?: boolean;
};

export const Dropdown = React.forwardRef(
  ({ onOpenChange, closeOnSelect = true, placement, disabled, ...styledProps }: DropdownProps, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
      placement
    });

    React.useEffect(() => {
      if (onOpenChange) {
        onOpenChange(isOpen);
      }
    }, [isOpen, onOpenChange]);

    React.useImperativeHandle(ref, () => ({
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false)
    }));

    return (
      <DropdownContext.Provider
        value={{
          isOpen,
          setIsOpen,
          closeOnSelect,
          disabled,
          setReferenceElement,
          setPopperElement,
          styles,
          attributes
        }}
      >
        <S.StyledContainer {...styledProps} />
      </DropdownContext.Provider>
    );
  }
);

Dropdown.defaultProps = {
  placement: 'bottom-end',
  zIndex: 10
};

const DropdownToggle = ({ children, withCaret, ...rest }: any) => {
  const { isOpen, setIsOpen, disabled, setReferenceElement } = useContext(DropdownContext);
  return (
    <S.StyledToggle
      ref={setReferenceElement}
      disabled={disabled}
      isOpen={isOpen}
      onClick={() => !disabled && setIsOpen(!isOpen)}
      {...rest}
    >
      {children}
      {withCaret && <S.StyledCaret size={20} className="dropdown-caret" />}
    </S.StyledToggle>
  );
};

const DropdownMenu = (props: any) => {
  const { isOpen } = useContext(DropdownContext);
  return isOpen ? <InnerMenu {...props} /> : null;
};

const InnerMenu = ({ children, ...rest }: any) => {
  const { setIsOpen, setPopperElement, styles, attributes } = useContext(DropdownContext);
  const menuRef = React.useRef<HTMLUListElement>(null);
  const handleClickOutside = React.useCallback(
    (event: Event) => {
      if (menuRef && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    },
    [setIsOpen]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div ref={setPopperElement} style={styles.popper} {...attributes.popper} css={{ marginTop: '10px', zIndex: 10000 }}>
      <S.StyledMenu ref={menuRef} {...rest}>
        {children}
      </S.StyledMenu>
    </div>
  );
};

interface IItemProps {
  onClick?: (...args: any) => void;
  children: any;
  [key: string]: any;
}

const DropdownItem = ({ children, onClick, closeOnItemSelect = true, ...rest }: IItemProps) => {
  const { setIsOpen, closeOnSelect } = useContext(DropdownContext);
  const handleClick = event => {
    if (onClick) {
      onClick(event);
    }
    if (closeOnSelect && closeOnItemSelect) {
      setIsOpen(false);
    }
  };
  return (
    <S.StyledItem onClick={handleClick} {...rest}>
      {children}
    </S.StyledItem>
  );
};

Dropdown.Toggle = DropdownToggle;
Dropdown.Menu = DropdownMenu;
Dropdown.Item = DropdownItem;
Dropdown.Delimiter = props => <Delimiter my="4px" px={12} {...props} />;
